import React, { useState, useEffect, useMemo } from 'react';
import { Button, Input } from './ui';
import { UserRole } from '../types';

interface ProfileSetupProps {
  onComplete: (profile: { name: string; role: UserRole; avatar: string }) => void;
  roomName: string;
}

const ProfileSetup: React.FC<ProfileSetupProps> = ({ onComplete, roomName }) => {
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole | undefined>();
  const [avatar, setAvatar] = useState(0); // Index of selected avatar
  const isRoomCreator = localStorage.getItem('isRoomCreator') === 'true';

  // Predefined cartoon avatar collection
  const avatarCollection = useMemo(() => [
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix&backgroundColor=b6e3f4,c0aede,d1d4f9',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka&backgroundColor=b6e3f4,c0aede,d1d4f9',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Gizmo&backgroundColor=b6e3f4,c0aede,d1d4f9',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Angel&backgroundColor=b6e3f4,c0aede,d1d4f9',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Shadow&backgroundColor=b6e3f4,c0aede,d1d4f9',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Salem&backgroundColor=b6e3f4,c0aede,d1d4f9',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Fluffy&backgroundColor=b6e3f4,c0aede,d1d4f9',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Boots&backgroundColor=b6e3f4,c0aede,d1d4f9',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Sophie&backgroundColor=b6e3f4,c0aede,d1d4f9',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Luna&backgroundColor=b6e3f4,c0aede,d1d4f9',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Oliver&backgroundColor=b6e3f4,c0aede,d1d4f9',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Marble&backgroundColor=b6e3f4,c0aede,d1d4f9'
  ], []);

  // Load from localStorage
  useEffect(() => {
    const savedProfile = localStorage.getItem('planningPokerProfile');
    if (savedProfile) {
      const profile = JSON.parse(savedProfile);
      setName(profile.name || '');
      setRole(profile.role);
      // Find avatar index or use random
      const avatarIndex = avatarCollection.findIndex(url => url === profile.avatar);
      setAvatar(avatarIndex >= 0 ? avatarIndex : Math.floor(Math.random() * avatarCollection.length));
    } else {
      // Set random avatar on first load
      setAvatar(Math.floor(Math.random() * avatarCollection.length));
    }
  }, [avatarCollection]);

  const handleComplete = () => {
    if (!name.trim() || !role) return;

    const profile = { name: name.trim(), role, avatar: avatarCollection[avatar] };

    // Save to localStorage
    localStorage.setItem('planningPokerProfile', JSON.stringify(profile));

    onComplete(profile);
  };

  const handleCancel = () => {
    // Clear room session data if canceling
    localStorage.removeItem('currentRoomId');
    localStorage.removeItem('currentRoomName');
    localStorage.removeItem('isRoomCreator');
    localStorage.removeItem('needsProfileForNewRoom');

    // Navigate to create page
    window.location.href = '/create';
  };

  const isValid = name.trim() && role;

  const roleOptions: { value: UserRole; label: string }[] = [
    { value: 'Frontend', label: 'Frontend' },
    { value: 'UI/UX', label: 'UI/UX' },
    { value: 'Backend', label: 'Backend' },
    { value: 'QA', label: 'QA' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 backdrop-blur-3xl"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%2523ffffff%22%20fill-opacity%3D%220.03%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center min-h-screen px-8 py-16 relative z-10">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-normal text-white mb-2">
            {isRoomCreator ? (
              <>Get Ready to Host <span className="font-semibold">Scromu Session</span></>
            ) : (
              <>Get Ready to Join <span className="font-semibold">Scromu Session</span></>
            )}
          </h1>
          <p className="text-lg text-purple-200">
            {isRoomCreator
              ? 'Set up your profile to create and lead your estimation room.'
              : 'Set up your profile to join the estimation room.'}
          </p>
        </div>

        {/* Profile Setup Card */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl max-w-sm w-full relative">
          {/* Room Name Header */}
          <div className="flex items-center justify-center gap-3 p-2 mb-6">
            {/* Logo Cards */}
            <div className="relative">
              <div className="w-6 h-8 bg-yellow-200 rounded border border-yellow-400 flex items-center justify-center">
                <span className="text-yellow-600 text-xs font-semibold">5</span>
                <div className="absolute top-0.5 left-0.5 w-1 h-1 bg-yellow-600 rounded-full" />
                <div className="absolute bottom-0.5 right-0.5 w-1 h-1 bg-yellow-600 rounded-full" />
              </div>
              <div className="absolute -right-2 -top-0.5 w-6 h-8 bg-gray-800 rounded border border-gray-800 flex items-center justify-center rotate-12">
                <span className="text-white text-xs font-semibold">1</span>
                <div className="absolute top-0.5 left-0.5 w-1 h-1 bg-white rounded-full" />
                <div className="absolute bottom-0.5 right-0.5 w-1 h-1 bg-white rounded-full" />
              </div>
            </div>
            <span className="text-sm font-semibold text-white">{roomName}</span>
          </div>

          <div className="space-y-6">
            {/* Profile Avatar Section */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <img
                  src={avatarCollection[avatar]}
                  alt="Profile"
                  className="w-20 h-20 rounded-full object-cover"
                />
                {/* Random button overlay */}
                <button
                  onClick={() => setAvatar(Math.floor(Math.random() * avatarCollection.length))}
                  className="absolute !text-white -bottom-2 -right-2 w-8 h-8 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center border border-white/20 hover:bg-white/20 transition-colors"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M16 4L20 8L8 20L4 20L4 16L16 4Z" fill="#ffff" stroke="#ffff" strokeWidth="1"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Name Input */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-purple-200 mb-1">
                Your Name
              </label>
              <Input
                id="name"
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full bg-white/10 border-white/30 text-white placeholder-blue-200/50"
              />
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-sm font-semibold text-purple-200 mb-1">
                Your Role
              </label>
              <div className="grid grid-cols-2 gap-3">
                {roleOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setRole(option.value)}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      role === option.value
                        ? 'border-white/70 bg-white/50 text-white backdrop-blur-xl'
                        : 'border-white/20 bg-white/10 text-purple-200 hover:border-white/30 hover:bg-white/20 backdrop-blur-xl'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                onClick={handleCancel}
                className="flex-1 bg-white/10 !text-sm text-white border border-white/30 hover:bg-white/20 backdrop-blur-xl transition-colors"
              >
                Cancel
              </Button>

              <Button
                type="button"
                onClick={handleComplete}
                disabled={!isValid}
                className="flex-1 text-white !text-sm bg-gradient-to-br from-purple-500 to-blue-500  flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRoomCreator ? 'Create Session' : 'Join Session'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetup;
